import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { type StackProps, XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

type SidebarFooterLineProps = Omit<StackProps, 'onClick'> & {
  icon: ReactNode;
  label: ReactNode;
  labelClassName?: string;
  labelRef?: Ref<HTMLSpanElement>;
  description?: ReactNode;
  trailing?: ReactNode;
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  stretch?: boolean;
};

export const SidebarFooterLine = ({
  icon,
  label,
  labelClassName,
  labelRef,
  description,
  trailing,
  href,
  onClick,
  stretch,
  className,
  ...props
}: SidebarFooterLineProps) => {
  const interactive = !!href || !!onClick;
  const mainClassName = cn(
    'flex flex-1 min-w-0 min-h-8 items-center gap-1 pl-[2px] pr-1 rounded-lg text-left',
    interactive &&
      'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring',
    interactive && !stretch && 'hover:bg-secondary-hover transition-colors',
    stretch &&
      'after:absolute after:inset-0 after:rounded-xl focus-visible:ring-0 focus-visible:after:ring-2 focus-visible:after:ring-ring'
  );
  const content = (
    <>
      <XStack center className="size-6 shrink-0">
        {icon}
      </XStack>
      <YStack className="flex-1 min-w-0">
        <span ref={labelRef} className={cn('truncate', labelClassName)}>
          {label}
        </span>
        {description ? (
          <span className="truncate text-xs text-foreground-muted">
            {description}
          </span>
        ) : null}
      </YStack>
    </>
  );

  return (
    <XStack className={cn('items-center gap-0.5', className)} {...props}>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={mainClassName}
          onClick={onClick}
        >
          {content}
        </a>
      ) : onClick ? (
        <button type="button" className={mainClassName} onClick={onClick}>
          {content}
        </button>
      ) : (
        <XStack className={mainClassName}>{content}</XStack>
      )}
      {trailing ? (
        <XStack className="relative z-10 items-center gap-0.5">
          {trailing}
        </XStack>
      ) : null}
    </XStack>
  );
};

export const SidebarFooterDismissButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'size-8 shrink-0 flex items-center justify-center rounded-lg',
      'hover:bg-secondary-hover transition-colors',
      'outline-none focus-visible:ring-2 focus-visible:ring-ring',
      className
    )}
    {...props}
  >
    <Icons.cancel className="size-3.5 text-foreground-muted" />
  </button>
));
SidebarFooterDismissButton.displayName = 'SidebarFooterDismissButton';

type SidebarFooterRowProps = Omit<SidebarFooterLineProps, 'stretch'> & {
  onDismiss?: () => void;
  dismissLabel?: string;
};

export const SidebarFooterRow = forwardRef<
  HTMLDivElement,
  SidebarFooterRowProps
>(
  (
    {
      icon,
      label,
      labelClassName,
      labelRef,
      description,
      trailing,
      href,
      onClick,
      onDismiss,
      dismissLabel = 'Dismiss',
      className,
      children,
      ...props
    },
    ref
  ) => (
    <YStack
      ref={ref}
      className={cn(
        'relative w-full p-1 rounded-xl bg-surface-hover text-sm text-foreground-subtle',
        (href || onClick) && 'hover:bg-secondary-hover transition-colors',
        className
      )}
      {...props}
    >
      <SidebarFooterLine
        icon={icon}
        label={label}
        labelClassName={labelClassName}
        labelRef={labelRef}
        description={description}
        href={href}
        onClick={onClick}
        stretch={!!href || !!onClick}
        trailing={
          <>
            {trailing}
            {onDismiss ? (
              <SidebarFooterDismissButton
                aria-label={dismissLabel}
                onClick={onDismiss}
              />
            ) : null}
          </>
        }
      />
      {children}
    </YStack>
  )
);
SidebarFooterRow.displayName = 'SidebarFooterRow';
